import { Component } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { CareerPathService } from '../../../../core/services/career-path.service';
import { CategoryMappingService } from '../../../../core/services/category-mapping.service';
import { CommonModule } from '@angular/common';
import { TreeModule } from 'primeng/tree';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { EvaluationFilterService } from '../../../../core/services/evaluation-filter.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

import { buildCategoryTree, practiceOfCategoryKey, resolveSelection } from '../../../../core/utils/category-tree';

@Component({
  selector: 'em-evaluation-form',
  standalone: true,
  imports: [
    ToastModule,
    TreeModule,
    CommonModule,
    FormsModule,
    CheckboxModule,
    ButtonModule,
    TranslatePipe,
  ],
  templateUrl: './evaluation-form.component.html',
  styleUrl: './evaluation-form.component.scss'
})
export class EvaluationFormComponent {
  careerPaths!: any;
  categories!: any;
  generalCategories!: any;
  technicalCategories!: any;
  bsaCategories!: any;
  bpsCategories!: any;
  managemenetCategories!: any;
  selected: any[] = [];
  saving = false;
  translations: any = {};
  /**
   * Practice a Manager / Manager People is confined to. Null for everyone else.
   * When set, the tree shows only that practice and saving merges the selection
   * back onto the untouched entries of the other practices.
   */
  private readonly scopedPractice: string | null;

  /**
   * How many actual categories are selected.
   *
   * PrimeNG adds fully selected parents to the selection alongside their
   * children, so a plain length would count practices and career paths too.
   * Only leaves carry shortLabel.
   */
  get selectedCategoryCount(): number {
    return this.selected.filter((node) => node?.shortLabel).length;
  }

  constructor(
    private careerPathService: CareerPathService,
    private categoryService: CategoryMappingService,
    private evaluationFilterService: EvaluationFilterService,
    private translationService: TranslateService,
    public ref: DynamicDialogRef,
    config: DynamicDialogConfig,
  ) {
    this.scopedPractice = (config?.data?.scopedPractice ?? null) as string | null;
  }
  
  ngOnInit(): void {
    this.loadCareerPathsAndSelection();
    this.loadTranslations();
  }

  private loadTranslations() {
    this.translationService.get([
      'layout.configurations.evaluation.addEvaluationFilter.save',
    ]).subscribe((response) => {
      this.translations = response;
    })
  }

  private loadCareerPathsAndSelection(): void {
    // Practice -> Career Path -> Category, shared with the pool filter. Both used
    // to list career paths at the top level, mixing practices together with no
    // way to tell which practice a career path belonged to.
    //
    // Loaded together because the saved selection has to be resolved against the
    // finished tree; as two separate subscriptions whichever finished last won.
    forkJoin({
      careerPaths: this.careerPathService.getAll(),
      categories: this.categoryService.getAll(),
      savedFilter: this.evaluationFilterService.getEvaluationFilter(),
    }).subscribe(({ careerPaths, categories, savedFilter }) => {
      // A scoped user only ever sees their own practice, so the tree is pruned to
      // it and no practice picker is needed.
      const scopedCareerPaths = this.scopedPractice === null
        ? careerPaths
        : (careerPaths ?? []).filter((cp: any) => this.isScopedPractice(cp?.practice));
      const scopedCategories = this.scopedPractice === null
        ? categories
        : (categories ?? []).filter((c: any) => this.isScopedPractice(c?.careerPath?.practice));

      this.careerPaths = buildCategoryTree(scopedCareerPaths, scopedCategories);

      if (!savedFilter?.filterJson) {
        return;
      }

      // Resolve to the real tree nodes: PrimeNG matches selection by object
      // identity, so the plain objects this used to build were never recognised
      // and restored ticks did not count towards their parent.
      const savedKeys = (JSON.parse(savedFilter.filterJson) as Array<{ key: string }>)
        .map(item => item.key);
      this.selected = resolveSelection(this.careerPaths, savedKeys);
    });
  }

  private isScopedPractice(practice: string | null | undefined): boolean {
    return this.scopedPractice !== null
      && (practice ?? '').trim().toLowerCase() === this.scopedPractice.toLowerCase();
  }


  public submitData(): void {
    // Keep only category leaves. p-treeSelect reports ticked parents alongside
    // their children, so without this the new Practice and Career Path branches
    // would be saved as if they were categories, with an undefined shortLabel.
    const selection = this.selected
      .filter(selected => selected?.shortLabel && selected?.careerPath)
      .map(selected => ({
        key: selected.key,
        label: selected.label,
        // The practice is what tells a category apart from another practice' one.
        // It is only added when the tree knows it, so the stored entries keep
        // their previous shape.
        ...(selected.practice ? { practice: selected.practice } : {}),
        shortLabel: selected.shortLabel
      }));

    if (this.scopedPractice === null) {
      this.saveAndClose(selection, selection);
      return;
    }

    // A scoped user owns only their practice: merge their selection with the other
    // practices' entries the dialog never showed, so saving cannot drop another
    // practice's configuration.
    this.evaluationFilterService.getEvaluationFilter().subscribe((response) => {
      const others = this.parseExisting(response?.filterJson)
        .filter(entry => !this.isScopedPractice(entry?.practice ?? practiceOfCategoryKey(entry?.key)));
      const merged = [...others, ...selection];
      this.saveAndClose(merged, selection);
    });
  }

  /** Persists the merged list but reports back only this user's own selection. */
  private saveAndClose(toSave: any[], reported: any[]): void {
    const filter = JSON.stringify(toSave);
    // Close only once the selection is stored: the page reloads the tabs that depend
    // on it (e.g. the Remuneration & Benefits grid) as soon as the dialog closes.
    this.saving = true;
    this.evaluationFilterService.updateEvaluationFilter(filter).subscribe({
      next: () => this.ref.close({ filters: reported, saved: true }),
      error: () => {
        this.saving = false;
        this.ref.close({ saved: false, error: true });
      },
    });
  }

  private parseExisting(filterJson: string | null | undefined): any[] {
    try {
      const parsed = JSON.parse(filterJson || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}